import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, ArrowRight, Star } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section className="relative overflow-hidden bg-white" data-testid="hero-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231e3a5f' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="vf-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center py-20 lg:py-32">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span className="vf-badge-compliance" data-testid="hero-badge">
                <ShieldCheck className="w-4 h-4" />
                {t('hero_badge')}
              </span>
            </motion.div>

            {/* Headline */}
            <div>
              <h1 className="vf-heading-1 text-[#1e3a5f]">
                {t('hero_title')}
                <br />
                <span className="text-[#22c55e]">{t('hero_title_2')}</span>
              </h1>
            </div>

            {/* Subtitle */}
            <p className="vf-body-large text-slate-600 max-w-xl">
              {t('hero_subtitle')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/register?plan=pos"
                className="vf-btn-primary flex items-center justify-center gap-2"
                data-testid="hero-cta-pos"
              >
                {t('hero_cta_pos')}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/register?plan=invoice"
                className="vf-btn-secondary flex items-center justify-center gap-2"
                data-testid="hero-cta-invoice"
              >
                {t('hero_cta_invoice')}
              </Link>
            </div>

            {/* Trust Badge */}
            <div className="flex items-center gap-4 pt-4">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center overflow-hidden"
                  >
                    <span className="text-xs font-medium text-slate-500">{i}</span>
                  </div>
                ))}
              </div>
              <div className="text-sm text-slate-600">
                <div className="flex items-center gap-1 text-yellow-500">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <span>{t('hero_trusted')}</span>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Image/Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="relative">
              {/* Main Image Container */}
              <div className="relative bg-[#1e3a5f] rounded-sm p-2 shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1715635846028-f162249377c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxyZXRhaWwlMjBzaG9wJTIwb3duZXIlMjB1c2luZyUyMHRhYmxldCUyMHBvcyUyMHN5c3RlbXxlbnwwfHx8fDE3NzMyMjIwMTd8MA&ixlib=rb-4.1.0&q=85"
                  alt="Shop owner using VendFactura POS"
                  className="w-full h-auto object-cover"
                  data-testid="hero-image"
                />
              </div>

              {/* Floating Card - Compliance */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute -bottom-6 -left-6 bg-white p-4 border-2 border-[#22c55e] shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-[#22c55e]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#1e3a5f]">VeriFactu</p>
                    <p className="text-sm text-green-600">100% Compliant</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Card - Price */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="absolute -top-4 -right-4 bg-[#22c55e] text-white p-4 shadow-lg vf-pulse-green"
              >
                <p className="text-xs font-bold uppercase tracking-wider">Desde</p>
                <p className="text-2xl font-bold">€4.99<span className="text-sm font-normal">/mes</span></p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
